import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";
import { Badge } from "./badge";

/** What a tile's figure says about the state of things. */
export type StatTileTone = "none" | "positive" | "warning" | "critical";

const STRIPE: Record<StatTileTone, string> = {
  none: "",
  positive: "border-t-3 border-t-positive",
  warning: "border-t-3 border-t-warning",
  critical: "border-t-3 border-t-critical",
};

export interface StatTileProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  label: string;
  value: ReactNode;
  /** One short line under the figure. */
  note?: ReactNode;
  tone?: StatTileTone;
  /** The state in a word — "Healthy", "Fix" — drawn as a badge in the tone. */
  status?: string;
  /** A tile that has no figure yet shows it muted rather than as a number. */
  muted?: boolean;
}

/**
 * One headline figure on its own surface: a label, the number, and a line
 * under it. A tone draws a stripe across the top and, with `status`, a badge
 * that says the same thing in words.
 */
export function StatTile({
  label,
  value,
  note,
  tone = "none",
  status,
  muted = false,
  className,
  children,
  ...props
}: StatTileProps) {
  return (
    <div
      data-tone={tone}
      className={cn(
        "flex min-w-0 flex-col gap-2 rounded-[var(--radius-card)] border border-rule/80 bg-surface p-5",
        STRIPE[tone],
        className,
      )}
      {...props}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-micro tracking-[0.08em] text-ink-3 uppercase">
          {label}
        </span>
        {status ? <Badge tone={tone === "none" ? "neutral" : tone}>{status}</Badge> : null}
      </div>
      <div
        className={cn(
          "tabular-nums",
          muted
            ? "text-prose font-medium text-ink-3"
            : "font-mono text-figure font-medium text-ink",
        )}
      >
        {value}
      </div>
      {note ? <p className="text-meta text-ink-3">{note}</p> : null}
      {children}
    </div>
  );
}
